import { localizeWorkspaceError } from '@/lib/workspace-error-copy';
import { companyRequest, CompanyRequestError } from '@/company/api';
import { apiUrl, authHeaders } from '@/lib/shell';
import { namespaceCopyFor } from '@/i18n/ns';
import workspaceKnowledgeRuntimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import {
  parseAccept,
  parseHistory,
  parsePageRevision,
  parsePageView,
  parsePreview,
  parseProposalDetail,
  parseProposalList,
  parseTree,
  parseWrite,
  UnexpectedPagesResponse,
} from './parse';

const root = '/api/v1/company/knowledge';
const segment = (id: string) => encodeURIComponent(id);
function query(params: Record<string, string | number | null | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params))
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  const text = search.toString();
  return text ? `?${text}` : '';
}
const get = (path: string) => companyRequest<unknown>(`${root}${path}`);
const send = (method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown) =>
  companyRequest<unknown>(`${root}${path}`, method, body);
/** Optional fields are left out rather than sent empty: the server reads an absent field as unset. */
const present = <T extends Record<string, unknown>>(body: T) =>
  Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined && value !== ''),
  );

export interface SaveInput {
  /** The revision the editor started from; null when the page has no text yet. */
  baseRevisionId: string | null;
  markdown: string;
  title?: string;
  /** A publisher's optional note, shown in the page history. */
  note?: string;
  /** Required when the save becomes a suggestion (anyone without publish rights). */
  reason?: string;
}

/** The one client for the company knowledge wiki. */
export const knowledgePagesApi = {
  /** The whole tree; reading it creates the company's root page on first use. */
  tree: () => get('/tree').then(parseTree),
  view: (id: string) => get(`/pages/${segment(id)}/view`).then(parsePageView),
  revision: (id: string, revisionId: string) =>
    get(`/pages/${segment(id)}${query({ revision: revisionId })}`).then(parsePageRevision),
  history: (id: string) => get(`/pages/${segment(id)}/history`).then(parseHistory),
  /** What suggested edits would do to a page's current text; nothing is stored. */
  preview: (id: string, edits: unknown[]) =>
    send('POST', `/pages/${segment(id)}/preview`, { edits }).then(parsePreview),
  create: (input: {
    title: string;
    parentId: string | null;
    markdown?: string;
    note?: string;
    reason?: string;
  }) =>
    send('POST', '/pages', {
      ...present({ ...input, parentId: undefined }),
      parentId: input.parentId,
    }).then(parseWrite),
  save: (id: string, input: SaveInput) =>
    send('PUT', `/pages/${segment(id)}`, {
      ...present({ ...input, baseRevisionId: undefined }),
      baseRevisionId: input.baseRevisionId,
    }).then(parseWrite),
  move: (id: string, input: { parentId: string | null; reason?: string }) =>
    send('POST', `/pages/${segment(id)}/move`, {
      ...present({ reason: input.reason }),
      parentId: input.parentId,
    }).then(parseWrite),
  archive: (id: string, input: { reason?: string } = {}) =>
    send('POST', `/pages/${segment(id)}/archive`, present(input)).then(parseWrite),
  /** A publisher brings an archived page back where it was. */
  unarchive: (id: string) => send('POST', `/pages/${segment(id)}/unarchive`, {}).then(parseWrite),
  /** A publisher deletes an archived page, its history and its suggestions for good. */
  remove: (id: string) => send('DELETE', `/pages/${segment(id)}`).then(parseWrite),
  /** `revisionId` is the revision the reviewer saw; a newer one answers 409. */
  accept: (id: string, revisionId: string | null, restoreArchived = false) =>
    send('POST', `/suggestions/${segment(id)}/accept`, {
      revisionId,
      ...(restoreArchived ? { restoreArchived: true } : {}),
    }).then(parseAccept),
  reject: (id: string) => send('POST', `/suggestions/${segment(id)}/reject`, {}),
  withdraw: (id: string) => send('POST', `/suggestions/${segment(id)}/withdraw`, {}),
  /** Pending suggestions the viewer may see: all for a publisher, their own otherwise. */
  proposals: () => get(`/page-proposals${query({ status: 'pending' })}`).then(parseProposalList),
  proposal: (id: string) => get(`/page-proposals/${segment(id)}`).then(parseProposalDetail),
  exportUrl: (id: string, revisionId?: string) =>
    apiUrl(`${root}/pages/${segment(id)}/export${query({ revision: revisionId })}`),
  download: (id: string, revisionId?: string) => downloadPage(id, revisionId),
};

// `attachment; filename="page.md"; filename*=UTF-8''Product%20direction.md`
function dispositionFilename(header: string | null) {
  const encoded = header?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = header?.match(/filename="([^"]+)"/i)?.[1];
  try {
    return (encoded && decodeURIComponent(encoded)) || plain || 'knowledge-page.md';
  } catch {
    return plain || 'knowledge-page.md';
  }
}

async function downloadPage(id: string, revisionId?: string) {
  const response = await fetch(knowledgePagesApi.exportUrl(id, revisionId), {
    credentials: 'same-origin',
    headers: authHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new CompanyRequestError(response.status, error.code ?? error.error ?? 'request_failed');
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = dispositionFilename(response.headers.get('content-disposition'));
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const KNOWLEDGE_ERROR_KEYS: Record<string, keyof typeof workspaceKnowledgeRuntimeCopy.en> = {
  revision_conflict: 'knowledgeErrorRevision_conflict',
  suggestion_outdated: 'knowledgeErrorSuggestion_outdated',
  suggestion_reason_required: 'knowledgeErrorSuggestion_reason_required',
  suggestion_no_change: 'knowledgeErrorSuggestion_no_change',
  suggestion_markdown_required: 'knowledgeErrorSuggestion_markdown_required',
  company_publish_required: 'knowledgeErrorCompany_publish_required',
  knowledge_proposal_limit: 'knowledgeErrorKnowledge_proposal_limit',
  knowledge_proposal_author_limit: 'knowledgeErrorKnowledge_proposal_author_limit',
  knowledge_page_too_large: 'knowledgeErrorKnowledge_page_too_large',
  knowledge_page_too_many_sections: 'knowledgeErrorKnowledge_page_too_many_sections',
  knowledge_page_limit: 'knowledgeErrorKnowledge_page_limit',
  knowledge_page_archived: 'knowledgeErrorKnowledge_page_archived',
  knowledge_page_has_children: 'knowledgeErrorKnowledge_page_has_children',
  knowledge_page_delete_children: 'knowledgeErrorKnowledge_page_delete_children',
  knowledge_page_not_archived: 'knowledgeErrorKnowledge_page_not_archived',
  knowledge_root_page: 'knowledgeErrorKnowledge_root_page',
  knowledge_page_cycle: 'knowledgeErrorKnowledge_page_cycle',
  knowledge_parent_not_found: 'knowledgeErrorKnowledge_parent_not_found',
  knowledge_parent_archived: 'knowledgeErrorKnowledge_parent_archived',
  knowledge_page_not_found: 'knowledgeErrorKnowledge_page_not_found',
  knowledge_proposal_not_found: 'knowledgeErrorKnowledge_proposal_not_found',
  knowledge_proposal_forbidden: 'knowledgeErrorKnowledge_proposal_forbidden',
  proposal_not_pending: 'knowledgeErrorProposal_not_pending',
  proposal_requester_removed: 'knowledgeErrorProposal_requester_removed',
  page_archived: 'knowledgeErrorPage_archived',
  invalid_knowledge_page_input: 'knowledgeErrorInvalid_knowledge_page_input',
  knowledge_pages_unavailable: 'knowledgeErrorKnowledge_pages_unavailable',
  knowledge_pages_timeout: 'knowledgeErrorKnowledge_pages_timeout',
};

/** User-facing copy for a failed wiki request; never raw server text. */
export function pagesErrorMessage(failure: unknown, fallback: string, locale?: string) {
  const copy = namespaceCopyFor(workspaceKnowledgeRuntimeCopy, locale);
  if (failure instanceof CompanyRequestError) {
    const key = KNOWLEDGE_ERROR_KEYS[failure.code];
    if (key) return copy[key];
    if (failure.status === 404 || failure.status === 413) return fallback;
    return localizeWorkspaceError(failure.message, locale);
  }
  if (failure instanceof UnexpectedPagesResponse) return fallback;
  return fallback;
}

export const errorCode = (failure: unknown) =>
  failure instanceof CompanyRequestError ? failure.code : null;
export const errorStatus = (failure: unknown) =>
  failure instanceof CompanyRequestError ? failure.status : null;
